import React, { useEffect, useState, useCallback } from 'react';
import { branchAPI, partyAPI } from '../../services/api';

const fmtErr = (detail, fallback) =>
  typeof detail === 'string' ? detail : fallback;

/**
 * Branches / departments admin: create branches, assign staff, and see how
 * today's workload is spread across each branch's nurses (the same numbers
 * the dispatch engine balances against).
 */
export default function Branches() {
  const [branches, setBranches] = useState([]);
  const [selected, setSelected] = useState(null);
  const [members, setMembers] = useState([]);
  const [workload, setWorkload] = useState([]);
  const [staff, setStaff] = useState([]);
  const [newCode, setNewCode] = useState('');
  const [newName, setNewName] = useState('');
  const [pickParty, setPickParty] = useState('');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'ok') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const loadBranches = useCallback(() => {
    branchAPI.list().then(r => setBranches(r.data || [])).catch(() => setBranches([]));
  }, []);

  useEffect(() => {
    loadBranches();
    // Assignable staff = nurses + doctors.
    Promise.all([
      partyAPI.list({ role: 'NURSE', per_page: 200 }),
      partyAPI.list({ role: 'DOCTOR', per_page: 200 }),
    ]).then(([n, d]) => {
      const seen = new Set();
      const merged = [...(n.data || []), ...(d.data || [])].filter(p => {
        if (seen.has(p.party_id)) return false;
        seen.add(p.party_id);
        return true;
      });
      setStaff(merged);
    }).catch(() => setStaff([]));
  }, [loadBranches]);

  const openBranch = (b) => {
    setSelected(b);
    setMembers([]);
    setWorkload([]);
    branchAPI.members(b.branch_id).then(r => setMembers(r.data || [])).catch(() => {});
    branchAPI.workload(b.branch_id).then(r => setWorkload(r.data || [])).catch(() => {});
  };

  const createBranch = async () => {
    if (!newCode.trim() || !newName.trim()) return;
    setBusy(true);
    try {
      await branchAPI.create({ code: newCode.trim(), name: newName.trim() });
      setNewCode(''); setNewName('');
      loadBranches();
      showToast('✅ Branch created');
    } catch (err) {
      showToast('❌ ' + fmtErr(err.response?.data?.detail, 'Could not create branch'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const addMember = async () => {
    if (!selected || !pickParty) return;
    setBusy(true);
    try {
      await branchAPI.addMember(selected.branch_id, { party_id: pickParty });
      setPickParty('');
      openBranch(selected);
      loadBranches();
      showToast('✅ Assigned to branch');
    } catch (err) {
      showToast('❌ ' + fmtErr(err.response?.data?.detail, 'Could not assign'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const removeMember = async (pid) => {
    setBusy(true);
    try {
      await branchAPI.removeMember(selected.branch_id, pid);
      openBranch(selected);
      loadBranches();
      showToast('Removed from branch');
    } catch (err) {
      showToast('❌ ' + fmtErr(err.response?.data?.detail, 'Could not remove'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const card = { background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' };
  const input = { padding: '8px 12px', border: '1px solid #ece3e7', borderRadius: 8, fontSize: 13 };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: '#2b1b22', margin: 0 }}>Branches & Departments</h1>
        <p style={{ color: '#8b7681', fontSize: 13, marginTop: 4 }}>
          Organise staff into branches · dispatch can scope its search to one branch · workload spreads across each team
        </p>
      </div>

      {toast && (
        <div style={{
          padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: 13, fontWeight: 600,
          background: toast.type === 'error' ? '#fee2e2' : '#d1fae5',
          color: toast.type === 'error' ? '#991b1b' : '#065f46',
        }}>{toast.msg}</div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 20, alignItems: 'start' }}>
        {/* Branch list + create */}
        <div style={card}>
          <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700, color: '#2b1b22' }}>Branches</h3>
          {branches.length === 0 && (
            <div style={{ fontSize: 12.5, color: '#a99aa0', marginBottom: 10 }}>
              No branches yet — create the first one below.
            </div>
          )}
          {branches.map(b => (
            <div
              key={b.branch_id}
              onClick={() => openBranch(b)}
              style={{
                padding: '10px 12px', borderRadius: 8, marginBottom: 6, cursor: 'pointer',
                border: `2px solid ${selected?.branch_id === b.branch_id ? '#94335a' : '#f1f5f9'}`,
                background: selected?.branch_id === b.branch_id ? '#fdeef5' : '#fafafa',
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 700, color: '#2b1b22' }}>{b.name}</div>
              <div style={{ fontSize: 11, color: '#8b7681' }}>
                {b.code} · {b.member_count} member{b.member_count === 1 ? '' : 's'}
              </div>
            </div>
          ))}
          <div style={{ borderTop: '1px solid #f1f5f9', marginTop: 12, paddingTop: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#8b7681', marginBottom: 8 }}>NEW BRANCH</div>
            <input style={{ ...input, width: '100%', marginBottom: 6, boxSizing: 'border-box' }}
                   placeholder="Code (e.g. KILIMANI)" value={newCode}
                   onChange={e => setNewCode(e.target.value)} />
            <input style={{ ...input, width: '100%', marginBottom: 8, boxSizing: 'border-box' }}
                   placeholder="Name (e.g. Kilimani Branch)" value={newName}
                   onChange={e => setNewName(e.target.value)} />
            <button onClick={createBranch} disabled={busy || !newCode.trim() || !newName.trim()} style={{
              width: '100%', padding: '9px', background: '#7b1e3c', color: '#fff',
              border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer',
            }}>+ Create branch</button>
          </div>
        </div>

        {/* Members + workload */}
        <div style={card}>
          {!selected ? (
            <div style={{ color: '#a99aa0', textAlign: 'center', padding: '50px 20px', fontSize: 13 }}>
              ← Select a branch to manage its team and see today's workload
            </div>
          ) : (
            <>
              <h3 style={{ margin: '0 0 4px', fontSize: 15, fontWeight: 800, color: '#2b1b22' }}>{selected.name}</h3>
              <div style={{ fontSize: 12, color: '#8b7681', marginBottom: 16 }}>{selected.code}</div>

              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                <select value={pickParty} onChange={e => setPickParty(e.target.value)}
                        style={{ ...input, flex: 1, background: '#fff' }}>
                  <option value="">Assign a nurse or doctor…</option>
                  {staff
                    .filter(p => !members.some(m => m.party_id === p.party_id))
                    .map(p => (
                      <option key={p.party_id} value={p.party_id}>{p.name}</option>
                    ))}
                </select>
                <button onClick={addMember} disabled={busy || !pickParty} style={{
                  padding: '8px 16px', background: '#10b981', color: '#fff', border: 'none',
                  borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer',
                }}>Assign</button>
              </div>

              <div style={{ fontSize: 11, fontWeight: 700, color: '#8b7681', marginBottom: 8 }}>
                TEAM & TODAY'S WORKLOAD
              </div>
              {members.length === 0 ? (
                <div style={{ fontSize: 12.5, color: '#a99aa0' }}>No members yet.</div>
              ) : (
                members.map(m => {
                  const w = workload.find(x => x.party_id === m.party_id) || {};
                  const load = (w.visits_today || 0) + (w.open_requests || 0);
                  return (
                    <div key={m.party_id} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '10px 12px', borderRadius: 8, background: '#fafafa', marginBottom: 6,
                    }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#2b1b22' }}>
                          {m.name}
                          <span style={{ marginLeft: 8, fontSize: 10.5, fontWeight: 600, color: '#94335a' }}>
                            {(m.roles || []).join(' · ')}
                          </span>
                        </div>
                        <div style={{ fontSize: 11, color: '#8b7681', marginTop: 2 }}>
                          🗓 {w.visits_today ?? 0} visit{(w.visits_today ?? 0) === 1 ? '' : 's'} today
                          {' '}· 📋 {w.open_requests ?? 0} open request{(w.open_requests ?? 0) === 1 ? '' : 's'}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{
                          fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 12,
                          background: load >= 4 ? '#fee2e2' : load >= 2 ? '#fef9c3' : '#d1fae5',
                          color: load >= 4 ? '#991b1b' : load >= 2 ? '#92400e' : '#065f46',
                        }}>
                          {load >= 4 ? 'Heavy' : load >= 2 ? 'Moderate' : 'Light'}
                        </span>
                        <button onClick={() => removeMember(m.party_id)} disabled={busy} style={{
                          padding: '4px 10px', background: 'none', border: '1px solid #ece3e7',
                          borderRadius: 6, fontSize: 11, color: '#991b1b', cursor: 'pointer',
                        }}>Remove</button>
                      </div>
                    </div>
                  );
                })
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
